<template>
  <div class="introduction-card">
    <!-- <div class="introduction-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
        </div> -->
    <h2>{{ t('registration.introduction.subtitle', { service: serviceTypeLabel }) }}</h2>
    <p class="introduction-description">
      {{ t('registrationType.' + serviceType + '.introduction.description', t('registration.introduction.description', { service: serviceTypeLabel })) }}
    </p>
    <p
      class="introduction-description"
      v-if="t('registrationType.' + serviceType + '.introduction.customDescription','').length > 0"
    >
      {{ t('registrationType.' + serviceType + '.introduction.customDescription', '') }}
    </p>
        
    <div class="introduction-features">
      <div
        class="feature-item"
        v-for="(feature, index) in features"
        :key="index"
      >
        <span class="feature-icon">✓</span>
        <span class="feature-text">{{ feature }}</span>
      </div>
    </div>
  </div>
  <div class="introduction-actions">
    <button
      @click="startRegistration"
      class="btn-primary"
    >
      {{ t('registration.introduction.startRegistration') }}
    </button>
    <button
      @click="goToLogin"
      class="btn-secondary"
    >
      {{ t('registration.introduction.alreadyHaveAccount') }}
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { r7eBaseApp } from '@r7e/shared';

const { t } = useI18n();

const serviceType = ref(r7eBaseApp.account_type);
const serviceTypeLabel = computed(() => t(`account_type.${serviceType.value}`));

const features = computed(() => {
  const serviceKey = serviceType.value;
  const baseKey = `registrationType.${serviceKey}.introduction.features`;
  
  // Try to get service-specific features, fallback to generic
  const featureKeys = [
    `${baseKey}.feature1`,
    `${baseKey}.feature2`,
    `${baseKey}.feature3`,
    `${baseKey}.feature4`,
    `${baseKey}.feature5`    
  ];
  
  return featureKeys
    .map(key => {
      const text = t(key, '');
      return text || null;
    })
    .filter(f => f !== null && f !== '');
});

const emit = defineEmits(['start-registration', 'go-to-login']);

const startRegistration = () => {
  // Emit event to parent to navigate to account setup
  emit('start-registration');
};

const goToLogin = () => {
  emit('go-to-login');
};

onMounted(() => {
  // Ensure we have the current service type
  serviceType.value = r7eBaseApp.account_type;
});
</script>

<style scoped>
.registration-introduction-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  background-color: var(--color-frame-bg);
  min-height: calc(100vh - 100px);
}

.introduction-header {
  text-align: center;
  margin-bottom: 40px;
}

.introduction-header h1 {
  color: var(--color-text-dark);
  margin-bottom: 20px;
  font-size: 2.5em;
}

.service-type-badge {
  display: inline-block;
  padding: 10px 24px;
  background-color: var(--color-safe);
  color: white;
  border-radius: 25px;
  font-size: 1.2em;
  font-weight: bold;
  text-transform: capitalize;
}

.introduction-content {
  background-color: white;
  padding: 40px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.introduction-card {
  text-align: center;
  margin-bottom: 40px;
}

.introduction-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 30px;
  color: var(--color-safe);
}

.introduction-icon svg {
  width: 100%;
  height: 100%;
}

.introduction-card h2 {
  color: var(--color-text-dark);
  margin-bottom: 20px;
  font-size: 1.8em;
}

.introduction-description {
  color: var(--color-text);
  font-size: 1.1em;
  line-height: 1.6;
  margin-bottom: 30px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.introduction-features {
  display: flex;
  flex-direction: column;
  /* gap: 15px; */
  max-width: 600px;
  margin: 0 auto;
  text-align: left;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: .5rem;
  padding: .2rem;
}

.feature-icon {
  color: var(--color-safe);
  font-size: 1.2em;
  font-weight: bold;
  flex-shrink: 0;
}

.feature-text {
  color: var(--color-text-dark);
  font-size: 1em;
}

.introduction-actions {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 40px;
  flex-wrap: wrap;
}

.btn-primary,
.btn-secondary {
  padding: 14px 32px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1.1em;
  font-weight: bold;
  transition: background-color 0.3s, transform 0.2s;
}

.btn-primary {
  background-color: var(--color-safe);
  color: var(--color-text);
}

.btn-primary:hover {
  background-color: var(--color-text);
  color: var(--color-safe);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.btn-secondary {
  background-color: transparent;
  color: var(--color-text);
  border: 2px solid var(--color-border);
}

.btn-secondary:hover {
  background-color: var(--color-border);
  transform: translateY(-2px);
}

/* Responsive design */
@media (max-width: 768px) {
  .registration-introduction-container {
    padding: 10px;
  }
  
  .introduction-content {
    padding: 20px;
  }
  
  .introduction-header h1 {
    font-size: 2em;
  }
  
  .introduction-card h2 {
    font-size: 1.4em;
  }
  
  .introduction-description {
    font-size: 1em;
  }
  
  .introduction-actions {
    flex-direction: column;
  }
  
  .btn-primary,
  .btn-secondary {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .introduction-header h1 {
    font-size: 1.5em;
  }
  
  .service-type-badge {
    font-size: 1em;
    padding: 8px 16px;
  }
}
</style>

